import { AxiosInstance } from "axios";
import { API_BASE_URL } from "./constants";
import { localCacheDB, ItemImageCacheRecord } from "../db/localCache";

export type ItemImageRole = "main" | string;

/** Gallery slots an item can hold, in display order. */
export const ITEM_IMAGE_ROLES = [
	"main", "front", "back", "left", "right", "top", "bottom",
	"detail-1", "detail-2", "detail-3", "lifestyle-1", "lifestyle-2", "packaging",
];

export const itemImageCacheKey = (itemId: number, role: ItemImageRole = "main") =>
	`${itemId}:${role}`;

export const itemImageRemoteUrl = (itemId: number, role: ItemImageRole = "main") =>
	`${API_BASE_URL}uploads/items/${itemId}-${role}.jpg`;

/** Reused object URLs so tiles share one URL per cached image. */
const objectUrlByKey = new Map<string, string>();

const toHex = (buffer: ArrayBuffer) =>
	Array.from(new Uint8Array(buffer))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");

export async function sha256Hex(data: ArrayBuffer): Promise<string> {
	const digest = await crypto.subtle.digest("SHA-256", data);
	return toHex(digest);
}

export async function hashBlob(blob: Blob): Promise<string> {
	const buf = await blob.arrayBuffer();
	return sha256Hex(buf);
}

function rememberObjectUrl(cacheKey: string, blob: Blob): string {
	const existing = objectUrlByKey.get(cacheKey);
	if (existing) {
		URL.revokeObjectURL(existing);
	}
	const url = URL.createObjectURL(blob);
	objectUrlByKey.set(cacheKey, url);
	return url;
}

export function getCachedObjectUrl(cacheKey: string): string | null {
	return objectUrlByKey.get(cacheKey) ?? null;
}

export async function getItemImageRecord(
	itemId: number,
	role: ItemImageRole = "main",
): Promise<ItemImageCacheRecord | undefined> {
	return localCacheDB.itemImages.get(itemImageCacheKey(itemId, role));
}

export async function listItemImageHashes(): Promise<
	Array<{ item_id: number; role: string; hash: string }>
> {
	const rows = await localCacheDB.itemImages.toArray();
	return rows.map((r) => ({
		item_id: r.itemId,
		role: r.role,
		hash: r.hash,
	}));
}

export async function putItemImageBlob(
	itemId: number,
	role: ItemImageRole,
	blob: Blob,
	sourceUrl?: string,
	knownHash?: string,
): Promise<ItemImageCacheRecord> {
	const hash = knownHash ?? (await hashBlob(blob));
	const cacheKey = itemImageCacheKey(itemId, role);
	const record: ItemImageCacheRecord = {
		cacheKey,
		itemId,
		role,
		hash,
		blob,
		mimeType: blob.type || "image/jpeg",
		updatedAt: new Date().toISOString(),
		sourceUrl,
	};
	await localCacheDB.itemImages.put(record);
	rememberObjectUrl(cacheKey, blob);
	return record;
}

/**
 * Download image from the static uploads path and store hash + blob in IDB.
 * Returns an object URL for display, or null if the server has no image.
 */
function isLikelyImageBlob(blob: Blob | null | undefined): blob is Blob {
	if (!blob || blob.size === 0) return false;
	// Accept explicit image MIME, or larger opaque bodies from some proxies.
	// Reject tiny JSON/HTML error payloads (e.g. Flask 404 message).
	if (blob.type.startsWith("image/")) return true;
	if (blob.type && !blob.type.startsWith("application/octet-stream")) return false;
	return blob.size > 500;
}

export async function fetchAndCacheItemImage(
	itemId: number,
	role: ItemImageRole = "main",
	api?: AxiosInstance,
): Promise<string | null> {
	const url = itemImageRemoteUrl(itemId, role);
	try {
		let blob: Blob | null = null;

		if (api) {
			try {
				// Never reject on 404 — missing images are normal and must not hit the
				// global axios error interceptor (toasts / login modal spam).
				const res = await api.get(`uploads/items/${itemId}-${role}.jpg`, {
					responseType: "blob",
					validateStatus: () => true,
					skipErrorToast: true,
				});
				if (res?.status >= 200 && res.status < 300 && isLikelyImageBlob(res.data)) {
					blob = res.data;
				}
			} catch {
				// fall through to bare fetch
			}
		}

		if (!blob) {
			const res = await fetch(url, { credentials: "include" });
			if (!res.ok) return null;
			const b = await res.blob();
			if (!isLikelyImageBlob(b)) return null;
			blob = b;
		}

		const record = await putItemImageBlob(itemId, role, blob, url);
		return getCachedObjectUrl(record.cacheKey);
	} catch {
		return null;
	}
}

/**
 * Memory → IDB only (no network). Used by Product Catalog gallery to avoid
 * probing every role over the wire; POS tiles / worker populate the cache.
 */
export async function resolveItemImageUrlFromCache(
	itemId: number,
	role: ItemImageRole = "main",
): Promise<string | null> {
	const cacheKey = itemImageCacheKey(itemId, role);
	const mem = getCachedObjectUrl(cacheKey);
	if (mem) return mem;

	const row = await getItemImageRecord(itemId, role);
	if (row?.blob) {
		return rememberObjectUrl(cacheKey, row.blob);
	}
	return null;
}

/**
 * Resolve display URL: memory → IDB → network. Never throws.
 */
export async function resolveItemImageUrl(
	itemId: number,
	role: ItemImageRole = "main",
	api?: AxiosInstance,
): Promise<{ url: string | null; fromCache: boolean }> {
	const cached = await resolveItemImageUrlFromCache(itemId, role);
	if (cached) return { url: cached, fromCache: true };

	const url = await fetchAndCacheItemImage(itemId, role, api);
	return { url, fromCache: false };
}

export type ImageSyncPayloadItem = {
	item_id: number;
	role: string;
	hash: string;
};

export type ImageSyncResponseItem = {
	item_id: number;
	role?: string;
	hash?: string;
	/** match | updated | missing | stale */
	status?: string;
	/** base64 image body when hash mismatch */
	data?: string;
	content_base64?: string;
	/** alternate: URL to re-download */
	url?: string;
};

/**
 * Send local hashes to the server. Server returns replacements when hashes differ.
 * Expected endpoint: POST items/images/sync
 * Body: { images: [{ item_id, role, hash }] }
 * Response: { images: [{ item_id, role, hash, status, data? }] }
 */
export async function syncItemImageHashes(api: AxiosInstance): Promise<{
	checked: number;
	updated: number;
	errors: number;
}> {
	const images = await listItemImageHashes();
	if (images.length === 0) {
		return { checked: 0, updated: 0, errors: 0 };
	}

	let updated = 0;
	let errors = 0;

	try {
		const res = await api.post("items/images/sync", { images });
		const body = res?.data;
		const list: ImageSyncResponseItem[] = Array.isArray(body?.images)
			? body.images
			: Array.isArray(body)
				? body
				: [];

		for (const entry of list) {
			const itemId = Number(entry.item_id);
			const role = entry.role || "main";
			const status = (entry.status || "").toLowerCase();

			// Unchanged — nothing to do
			if (status === "match" || status === "ok" || status === "unchanged") {
				continue;
			}

			try {
				const b64 = entry.data || entry.content_base64;
				if (b64) {
					const binary = atob(b64.includes(",") ? b64.split(",").pop()! : b64);
					const bytes = new Uint8Array(binary.length);
					for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
					const blob = new Blob([bytes], { type: "image/jpeg" });
					await putItemImageBlob(itemId, role, blob, undefined, entry.hash);
					updated += 1;
					continue;
				}

				if (entry.url || status === "updated" || status === "stale" || status === "mismatch") {
					const url = await fetchAndCacheItemImage(itemId, role, api);
					if (url) updated += 1;
					else errors += 1;
					continue;
				}

				if (status === "missing") {
					// Server no longer has image — drop local cache
					const key = itemImageCacheKey(itemId, role);
					const existing = objectUrlByKey.get(key);
					if (existing) {
						URL.revokeObjectURL(existing);
						objectUrlByKey.delete(key);
					}
					await localCacheDB.itemImages.delete(key);
				}
			} catch {
				errors += 1;
			}
		}
	} catch {
		// Endpoint may not exist yet — fail soft
		return { checked: images.length, updated: 0, errors: 1 };
	}

	return { checked: images.length, updated, errors };
}
